import { volumeApiFetch } from "@/lib/volume-api";
import { z } from "zod/v4";
import type { FetchBeneficiariesResponse } from "./fetch-optimus-beneficiaries-request";

// Matches ClearBankBeneficiaryResponseDto in payments-monolith
// (GET /api/clearbank/beneficiaries/for-application?applicationId=).
export const ClearBankBeneficiariesResponseSchema = z.array(
  z.object({
    id: z.string(),
    applicationId: z.string(),
    name: z.string(),
    sortCode: z.string().nullable(),
    accountNumber: z.string().nullable(),
    iban: z.string().nullable(),
    enabled: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string().nullable(),
    // Cross-border config (V309). Null on every Faster Payments beneficiary; optional so a response
    // from before these fields existed still parses.
    rail: z.string().nullable().optional(),
    creditorAgentBic: z.string().nullable().optional(),
  }),
);

// The monolith sends a payout cross-border exactly when the beneficiary's rail is this
// (CrossBorderBeneficiaryRules.isCrossBorder); absent and "FPS" both mean Faster Payments.
const CROSS_BORDER_RAIL = "CROSS_BORDER_GBP";
export type FetchClearBankBeneficiariesResponse = z.infer<
  typeof ClearBankBeneficiariesResponseSchema
>;

export async function fetchClearBankBeneficiariesForApplication({
  applicationId,
  accessToken,
}: {
  applicationId: string;
  accessToken: string;
}) {
  const response = await volumeApiFetch({
    accessToken,
    endpoint: "clearbank/beneficiaries/for-application",
    method: "GET",
    queryParameters: `applicationId=${applicationId}`,
  });

  if (response.status === 401 || response.status === 403) {
    throw new Error("Unauthorized");
  }
  if (!response.ok) {
    throw new Error("Failed to fetch clearbank beneficiaries");
  }
  const json = await response.json();
  return ClearBankBeneficiariesResponseSchema.parse(json);
}

/**
 * Adapts a ClearBank beneficiary into the destination-account shape the payout
 * form + DestinationAccountCard already render (the Optimus beneficiary shape),
 * so ClearBank beneficiaries can flow through the existing combo unchanged.
 * A cross-border beneficiary is presented as SWIFT: that is what puts it in the
 * payout form's International mode. The rail itself is decided by the monolith
 * from the beneficiary row, so the payout request is the same for both.
 */
export function clearBankBeneficiaryToDestinationAccount(
  b: FetchClearBankBeneficiariesResponse[number],
): FetchBeneficiariesResponse[number] {
  const hasScan = Boolean(b.sortCode && b.accountNumber);
  const isCrossBorder = b.rail === CROSS_BORDER_RAIL;
  return {
    applicationId: b.applicationId,
    // The combo keys items on optimusBeneficiaryId — carry the CB beneficiary id here.
    optimusBeneficiaryId: b.id,
    optimusExternalCustomerId: "",
    createdAt: b.createdAt,
    createdBy: "",
    updatedAt: b.updatedAt ?? b.createdAt,
    updatedBy: "",
    // Rendered as "Account Name" in the combo.
    firstNames: b.name,
    lastNames: "",
    businessName: null,
    contactDetails: {
      email: "",
      mobilePhone: null,
      homePhone: null,
      workPhone: null,
    },
    currency: "GBP",
    countryCode: "GB",
    // Cross-border routes on IBAN + BIC, so the IBAN is what travels even when a SCAN is also held.
    accountType: hasScan && !isCrossBorder ? "SCAN" : "IBAN",
    accountNumber: b.accountNumber,
    sortCode: b.sortCode,
    iban: b.iban,
    status: b.enabled ? "ACTIVE" : "DISABLED",
    // isUsableOptimusBeneficiary() filters on lifecycleStatus === "ACTIVE".
    lifecycleStatus: b.enabled ? "ACTIVE" : "DISABLED",
    address: null,
    bic: b.creditorAgentBic ?? null,
    lei: null,
    otherIdentifier: null,
    otherIdentifierType: null,
    isSwift: isCrossBorder,
    purposeCode: null,
    purposeCategoryCode: null,
    beneficiaryType: isCrossBorder ? "SWIFT" : "FASTER_PAYMENTS",
  };
}
